import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AdminShell, SectionHeader, ADMIN_SECTIONS } from '../components/admin/AdminShell';
import { OverviewSection } from '../components/admin/OverviewSection';
import { MembersSection } from '../components/admin/MembersSection';
import { DonationsPanel } from '../components/DonationsPanel';
import { ApplicationsPanel } from '../components/ApplicationsPanel';
import { ExportPanel } from '../components/ExportPanel';
import { AnnouncementsPanel } from '../components/AnnouncementsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'jbss-admin-token';
const sectionFromHash = () => {
  const h = window.location.hash.replace('#', '');
  return ADMIN_SECTIONS.some((s) => s.id === h) ? h : 'overview';
};

const LoginCard = ({ onToken }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('+91');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const call = async (fn) => { setBusy(true); setMsg(''); try { await fn(); } catch (e) { setMsg(e?.response?.data?.detail || 'Something went wrong'); } finally { setBusy(false); } };
  const sendOtp = () => call(async () => { await axios.post(`${API}/auth/send-otp`, { phone: phone.trim() }); setStep('otp'); setMsg('OTP sent. Check your SMS.'); });
  const verifyOtp = () => call(async () => {
    const res = await axios.post(`${API}/auth/verify-otp`, { phone: phone.trim(), code: code.trim() });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    onToken(res.data.token);
  });

  return (
    <div className="jbss-admin-auth">
      <div className="jbss-admin-card" data-testid="admin-login-card">
        <div className="jbss-admin-logo"><ShieldCheck size={28} /></div>
        <h1 className="jbss-admin-title">JBSS Admin</h1>
        <p className="jbss-admin-sub">Sign in with your registered mobile number</p>
        {step === 'phone' ? (
          <>
            <Label htmlFor="admin-phone" className="jbss-admin-label">Mobile number</Label>
            <Input id="admin-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+9198XXXXXXXX" data-testid="admin-phone-input" />
            <Button className="jbss-admin-btn" onClick={sendOtp} disabled={busy} data-testid="admin-send-otp">
              {busy ? <Loader2 className="jbss-spin" size={16} /> : null} Send OTP
            </Button>
          </>
        ) : (
          <>
            <Label htmlFor="admin-otp" className="jbss-admin-label">Enter OTP sent to {phone}</Label>
            <Input id="admin-otp" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" inputMode="numeric" data-testid="admin-otp-input" />
            <Button className="jbss-admin-btn" onClick={verifyOtp} disabled={busy} data-testid="admin-verify-otp">
              {busy ? <Loader2 className="jbss-spin" size={16} /> : null} Verify & Sign in
            </Button>
            <button className="jbss-admin-link" onClick={() => { setStep('phone'); setMsg(''); }} data-testid="admin-change-phone">Change number</button>
          </>
        )}
        {msg && <p className="jbss-admin-msg" data-testid="admin-auth-msg">{msg}</p>}
        <button className="jbss-admin-link" onClick={() => navigate('/')}>← Back to site</button>
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [section, setSection] = useState(sectionFromHash);
  const [approvals, setApprovals] = useState(0);
  const [pending, setPending] = useState(0);

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(''); }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => { logout(); setChecking(false); });
  }, [token, logout]);

  useEffect(() => {
    if (!token || checking) return;
    axios.get(`${API}/admin/applications`, { params: { status: 'pending' }, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPending(r.data.counts.pending)).catch(() => {});
  }, [token, checking, approvals, section]);

  const go = useCallback((id) => {
    if (window.location.hash !== `#${id}`) window.location.hash = id;
    setSection(id);
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const onHash = () => setSection(sectionFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (!token) return <LoginCard onToken={(t) => { setToken(t); setChecking(false); }} />;
  if (checking) return <div className="jbss-admin-auth"><Loader2 className="jbss-spin" size={28} /></div>;

  const common = { token, onUnauthorized: logout };
  const content = {
    overview: <OverviewSection {...common} onNavigate={go} />,
    members: <MembersSection {...common} />,
    applications: (
      <>
        <SectionHeader title="Applications" subtitle="Review people who applied through 'Become a Member'." />
        <ApplicationsPanel {...common} onApproved={() => setApprovals((n) => n + 1)} />
      </>
    ),
    donations: (
      <>
        <SectionHeader title="Donations" subtitle="Payments received through the website, with receipts." />
        <DonationsPanel {...common} />
      </>
    ),
    announcements: (
      <>
        <SectionHeader title="Announcements" subtitle="Send one WhatsApp message to approved members." />
        <AnnouncementsPanel {...common} refreshKey={approvals} />
      </>
    ),
    export: (
      <>
        <SectionHeader title="Export" subtitle="Download records as Excel. Leave dates empty for everything." />
        <ExportPanel {...common} />
      </>
    ),
  };

  return (
    <AdminShell active={section} onSelect={go} onViewSite={() => navigate('/')} onLogout={logout} badges={{ applications: pending }}>
      {content[section]}
    </AdminShell>
  );
};

export default Admin;
